// src/components/quotes/quoteTemplates.js
//
// "How do I start a new quote from something that already exists?" — the
// three answers, in one React-free module so each can be unit-tested:
//
//   • cloneSections / cloneLineItems — THE clone. Fresh id at every level,
//     the selected option resolved against the clones. Used by applyTemplate
//     (templates carry an INDEX) and by duplicateQuote (a live quote carries
//     an id). One implementation, both callers, so they cannot drift — the
//     same reason quantityOf has a parity test.
//   • buildWholeQuoteTemplate / buildSectionTemplate / buildItemsTemplate —
//     the three save modes. A whole-quote template IS a "sections" template
//     with more than one section, plus commonLineItems; older templates lack
//     commonLineItems and must keep applying (templateCommons).
//   • duplicateQuote — a copy built THROUGH makeQuoteRecord with an ALLOWLIST
//     of named fields, never a spread of the source.
//
// ⛔ ALLOWLIST, NOT BLOCKLIST. `{...oldQuote, id, status:"draft"}` plus a list
// of fields to null out is the obvious build and it fails silently later: a
// quote record carries fields the factory never creates (autotaskTicketId,
// autotaskTicketNumber, autotaskPushedAt, acceptanceReconciledAt, fulfilment
// on any record written before the factory gained it) and a blocklist only
// strips what its author knew about on the day. A copy that inherits
// acceptanceReconciledAt never flips to Won when the client accepts; one that
// inherits fulfilment.invoiced shows a green Invoiced chip on a quote never
// sent; one that inherits pdfUrl serves the OTHER client's PDF. Going through
// the factory means everything not named here — a fresh id, a freshly
// reserved Q number, status draft, null sentAt / decidedAt / pdfUrl /
// autotaskQuoteId, an empty fulfilment, and whatever the factory gains next —
// is right in copies automatically. quoteRecord.js exists for exactly this.
import { makeQuoteRecord } from "./quoteRecord";
import { seedQuoteTerms, makeClause } from "./quoteTerms";
import { isFreightSection } from "./quoteSectionOrder";

const rand = () => Math.random().toString(36).slice(2, 7);
export const DEFAULT_IDS = {
  section: () => `sec-${Date.now()}-${rand()}`,
  option: () => `opt-${Date.now()}-${rand()}`,
  lineItem: () => `li-${Date.now()}-${rand()}`,
};

/* Plain data only (line items carry strings, numbers, thumbnails as data
   URLs), so a JSON round-trip is a complete deep copy — the copy must share no
   object with the source, or editing one edits both. */
const deepCopy = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));

/** Line items with fresh ids and no shared references. */
export function cloneLineItems(items, ids = DEFAULT_IDS) {
  return (items || []).map((li) => ({ ...deepCopy(li), id: ids.lineItem() }));
}

/**
 * Sections with fresh ids at every level — section, option, line item — and
 * the selected option RESOLVED AGAINST THE CLONES. A template carries the
 * selection as `selectedOptionIdx` (the option id it pointed at never
 * survives a save); a live quote carries `selectedOptionId`, which is mapped
 * to an index against the SOURCE options first. Copying the id across would
 * leave the clone's selection pointing at the source's option: the section
 * renders with nothing selected, which looks like a data bug and isn't.
 */
export function cloneSections(sections, ids = DEFAULT_IDS) {
  return (sections || []).map((s) => {
    const srcOptions = s.options || [];
    const options = srcOptions.map((o) => ({
      id: ids.option(),
      label: o.label || "",
      description: o.description || "",
      lineItems: cloneLineItems(o.lineItems, ids),
    }));
    const idx = Number.isInteger(s.selectedOptionIdx)
      ? s.selectedOptionIdx
      : srcOptions.findIndex((o) => o.id === s.selectedOptionId);
    return {
      id: ids.section(),
      label: s.label || "New Section",
      description: s.description || "",
      selectedOptionId: options[idx]?.id || options[0]?.id || null,
      createdByAI: false,
      options,
    };
  });
}

// ── Templates ────────────────────────────────────────────────────────────────
// Ids are stripped throughout — they all regenerate on apply. Selection is
// carried as an INDEX because the option id it pointed at won't survive.

// eslint-disable-next-line no-unused-vars
const stripIds = (items) => (items || []).map(({ id, ...rest }) => deepCopy(rest));

function sectionToTemplate(sec) {
  const options = sec.options || [];
  return {
    label: sec.label,
    description: sec.description || "",
    selectedOptionIdx: Math.max(0, options.findIndex((o) => o.id === sec.selectedOptionId)),
    options: options.map((o) => ({
      label: o.label || "",
      description: o.description || "",
      lineItems: stripIds(o.lineItems),
    })),
  };
}

const templateId = () => `qtpl-${Date.now()}-${rand()}`;

/**
 * Whole quote: every section in order, every option and line item, plus the
 * common items. ⚠ EXCLUDES THE FREIGHT SECTION (autoCalculated): freight is
 * derived from the quote's own contents, so a copy would be wrong for every
 * quote the template is applied to — the target recalculates its own.
 * Terms are not carried: they are snapshotted per quote from Settings.
 */
export function buildWholeQuoteTemplate(quote, name) {
  return {
    id: templateId(),
    name,
    description: quote.description || "",
    shape: "sections",
    sections: (quote.sections || []).filter((s) => !isFreightSection(s)).map(sectionToTemplate),
    commonLineItems: stripIds(quote.commonLineItems),
    createdAt: Date.now(),
  };
}

/** One section with its options — "Turret Cameras: G4 Pro | G5 Pro" is a reusable unit on its own. */
export function buildSectionTemplate(quote, section, name) {
  return {
    id: templateId(),
    name,
    description: quote.description || "",
    shape: "sections",
    sections: [sectionToTemplate(section)],
    createdAt: Date.now(),
  };
}

/** A flat bag of line items, dropped into whatever location has focus on apply. */
export function buildItemsTemplate(quote, items, name) {
  return {
    id: templateId(),
    name,
    description: quote.description || "",
    shape: "flat",
    lineItems: stripIds(items),
    createdAt: Date.now(),
  };
}

// Templates come in two shapes:
//   "flat"     — a bag of line items, dropped into whatever location has focus.
//   "sections" — a section tree (one or many), merge-APPENDED as new sections.
// Templates saved before shapes existed have no `shape` field and are flat, so
// the check is `=== "sections"` and everything else falls through to flat.
export const isSectionTemplate = (t) => t?.shape === "sections" && Array.isArray(t.sections);

export const templateItemCount = (t) => (isSectionTemplate(t)
  ? t.sections.reduce((n, s) => n + (s.options || []).reduce((m, o) => m + (o.lineItems || []).length, 0), 0)
    + templateCommons(t).length
  : (t?.lineItems || []).length);

/** ⚠ Optional on read: every template saved before whole-quote templates lacks it. Never a reason to reject. */
export const templateCommons = (t) => (Array.isArray(t?.commonLineItems) ? t.commonLineItems : []);

/**
 * The structure a NEW quote starts with when created from a template — the
 * creation path applies exactly one template, once, without going through the
 * builder's additive apply. A flat template becomes one section with one
 * option so a blank quote has somewhere to put the items.
 */
export function structureFromTemplate(t, ids = DEFAULT_IDS) {
  if (!t) return { sections: [], commonLineItems: [] };
  if (isSectionTemplate(t)) {
    return { sections: cloneSections(t.sections, ids), commonLineItems: cloneLineItems(templateCommons(t), ids) };
  }
  const option = { id: ids.option(), label: "Option 1", description: "", lineItems: cloneLineItems(t.lineItems, ids) };
  return {
    sections: [{ id: ids.section(), label: t.name || "Items", description: "", selectedOptionId: option.id, createdByAI: false, options: [option] }],
    commonLineItems: [],
  };
}

// ── Duplicate ────────────────────────────────────────────────────────────────

/**
 * The clauses a copy carries beyond the default: the source's NON-required
 * ones, re-minted. The required clause comes from TODAY'S Settings — a copy is
 * a new offer, and quoteTerms.js is clear that the terms on an offer are what
 * the customer agrees to, so a copy must not go out under superseded wording.
 * (Craig's call, per the brief's recommendation: re-seed, plus carry additions.)
 */
export function duplicateTerms(sourceTerms, termsDefault) {
  const extras = (sourceTerms || []).filter((c) => c && !c.required && String(c.text || "").trim())
    .map((c) => makeClause(c.text));
  return [...seedQuoteTerms(termsDefault), ...extras];
}

/**
 * A copy of `src` (already migrated), through the ONE factory, naming every
 * field carried across. What deliberately does NOT come across: the Q number
 * (reserved fresh — a new quote is a new document; null if the counter is
 * unreachable, as the factory already handles), status / sentAt / decidedAt /
 * pdfUrl / Autotask fields / fulfilment / acceptance stamps (factory
 * defaults), backlogId / opportunityId / proposalId (the copy is not that
 * pipeline artifact), the freight section (recalculated), and anything held
 * server-side under the old id — acceptance, answers, open tracking — which a
 * new id simply does not have. Do not "helpfully" copy those.
 *
 * `overrides` is for the creation path (a copy started from a backlog item
 * takes that item's client and title); it is spread last.
 */
export async function duplicateQuote(src, { termsDefault, reserve, now = Date.now(), ids = DEFAULT_IDS, overrides = {} } = {}) {
  if (!src) throw new Error("duplicateQuote: no source quote");
  return makeQuoteRecord({
    seed: `${now}-${rand()}`,
    now,
    title: `${src.title || "New quote"} (copy)`,
    description: src.description || "",
    companyId: src.companyId || "",
    companyName: src.companyName || "",
    contactName: src.contactName || "",
    contactEmail: src.contactEmail || "",
    sections: cloneSections((src.sections || []).filter((s) => !isFreightSection(s)), ids),
    termsDefault,
    ...(reserve ? { reserve } : {}),
    extra: {
      commonLineItems: cloneLineItems(src.commonLineItems, ids),
      notes: src.notes || "",
      validUntil: src.validUntil || "",
      terms: duplicateTerms(src.terms, termsDefault),
      ...overrides,
    },
  });
}

/** Every id in a quote's tree — sections, options, line items — for the "no id survives a copy" assertion. */
export function treeIds(quote) {
  const out = new Set();
  if (quote?.id) out.add(quote.id);
  (quote?.sections || []).forEach((s) => {
    if (s.id) out.add(s.id);
    (s.options || []).forEach((o) => {
      if (o.id) out.add(o.id);
      (o.lineItems || []).forEach((li) => li.id && out.add(li.id));
    });
  });
  (quote?.commonLineItems || []).forEach((li) => li.id && out.add(li.id));
  return out;
}
